/*
 * Theme boot (plans/dark-mode.md): resolves the theme preference and stamps
 * it on <html> before first paint, so it must be loaded SYNCHRONOUSLY as
 * the first thing in <head> (no defer, no module) — views/partials/
 * theme-boot.njk is the include every root layout uses, and the BHSawesome,
 * BJC and Snap-embed chromes carry the same tag.
 *
 * Preferences (all localStorage on this origin; every surface below is
 * served from it):
 *
 *   theme            "light" / "dark", ABSENT meaning System — the SITE
 *                    preference, the contract BHSawesome's PreTeXt theme
 *                    established (its Reset All still clears this key).
 *   theme:bhsawesome  per-surface preferences: "light" / "dark" / "site"
 *   theme:bjc         (= follow the site). An absent key means the
 *   theme:snap        surface's DEFAULT: follow the site for the books,
 *                    but DARK for Snap! — Snap!'s own default look, what
 *                    students are used to. A value equal to the default
 *                    is stored as absence.
 *
 * Every page stamps its own surface's EFFECTIVE value as
 * data-theme="light|dark" (never absent after boot) plus the `dark-mode`
 * class BHSawesome's book.css keys off. Which surface a page is comes from
 * the script tag: <script src="/js/theme-boot.js" data-surface="bjc">;
 * no attribute means the site itself.
 *
 * Events on document:
 *   bhs:themechange          this page's effective value changed
 *   bhs:surfacethemechange   detail {surface, resolved} — fired per surface
 *                            whose effective value changed (a snap.ts host
 *                            page cares about 'snap', not its own surface)
 *
 * Controls (wired here so the logic lives in one file): radios named
 * theme-pref (site: system/light/dark) and theme-pref-<surface>
 * (site/light/dark), any [data-theme-toggle] button (cycles the site
 * preference; used by the lesson-planning/quiz-builder copies of this
 * script), and a [data-theme-match-site] button that clears every override.
 */

type ThemePref = 'system' | 'light' | 'dark';
type ThemeResolved = 'light' | 'dark';

const OVERRIDABLE = ['bhsawesome', 'bjc', 'snap'] as const;
type OverridableSurface = (typeof OVERRIDABLE)[number];
type Surface = 'site' | OverridableSurface;

type SurfacePref = 'site' | ThemeResolved;

interface BhsTheme {
  get(): ThemePref;
  set(pref: ThemePref): void;
  resolved(): ThemeResolved;
  getOverride(surface: OverridableSurface): SurfacePref;
  setOverride(surface: OverridableSurface, value: SurfacePref | null): void;
  resolvedFor(surface: Surface): ThemeResolved;
}

declare global {
  interface Window {
    bhsTheme: BhsTheme;
  }
}

const KEY = 'theme';

const isOverridable = (s: string): s is OverridableSurface =>
  (OVERRIDABLE as readonly string[]).includes(s);

const surfaceAttr = (document.currentScript as HTMLScriptElement | null)?.dataset.surface ?? '';
const PAGE_SURFACE: Surface = isOverridable(surfaceAttr) ? surfaceAttr : 'site';

// localStorage can throw (private windows, blocked site data); absent or
// unreadable both mean the default.
const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null): void => {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Nothing to do: the stamp below still applies for this page's lifetime.
  }
};

const getPref = (): ThemePref => {
  const v = read(KEY);
  return v === 'light' || v === 'dark' ? v : 'system';
};

// What "no stored value" means per surface: the books follow the site;
// Snap! is dark (its own default look, the one students know).
const SURFACE_DEFAULT: Record<OverridableSurface, SurfacePref> = {
  bhsawesome: 'site',
  bjc: 'site',
  snap: 'dark',
};

const getOverride = (surface: OverridableSurface): SurfacePref => {
  const v = read(`theme:${surface}`);
  return v === 'light' || v === 'dark' || v === 'site' ? v : SURFACE_DEFAULT[surface];
};

const media = window.matchMedia?.('(prefers-color-scheme: dark)');

const resolveSite = (): ThemeResolved =>
  getPref() === 'system' ? (media?.matches ? 'dark' : 'light') : (getPref() as ThemeResolved);

const resolvedFor = (surface: Surface): ThemeResolved => {
  if (surface === 'site') return resolveSite();
  const pref = getOverride(surface);
  return pref === 'site' ? resolveSite() : pref;
};

// Last-seen effective value per surface, for change events.
const last: Partial<Record<Surface, ThemeResolved>> = {};

const refresh = (): void => {
  refreshControls();
  for (const surface of ['site', ...OVERRIDABLE] as Surface[]) {
    const resolved = resolvedFor(surface);
    if (last[surface] === resolved) continue;
    const first = last[surface] === undefined;
    last[surface] = resolved;
    if (!first) {
      document.dispatchEvent(
        new CustomEvent('bhs:surfacethemechange', { detail: { surface, resolved } }),
      );
    }
  }
  const mine = last[PAGE_SURFACE] as ThemeResolved;
  const root = document.documentElement;
  if (root.getAttribute('data-theme') !== mine) {
    root.setAttribute('data-theme', mine);
    root.classList.toggle('dark-mode', mine === 'dark');
    document.dispatchEvent(new CustomEvent('bhs:themechange', { detail: { resolved: mine } }));
  }
};

window.bhsTheme = {
  get: getPref,
  set(pref: ThemePref) {
    write(KEY, pref === 'system' ? null : pref);
    refresh();
  },
  resolved: () => resolvedFor(PAGE_SURFACE),
  getOverride,
  setOverride(surface: OverridableSurface, value: SurfacePref | null) {
    // The default is stored as absence, so a surface left on its default
    // keeps following any future default change.
    const v = value ?? SURFACE_DEFAULT[surface];
    write(`theme:${surface}`, v === SURFACE_DEFAULT[surface] ? null : v);
    refresh();
  },
  resolvedFor,
};

refresh();

// Re-resolve on OS changes: only System-following surfaces move, which the
// per-surface comparison in refresh() sorts out.
media?.addEventListener('change', refresh);

// Another tab (or BHSawesome's Reset All) changed a preference.
window.addEventListener('storage', (e) => {
  if (e.key === null || e.key === KEY || e.key.startsWith('theme:')) refresh();
});

/* -- controls ------------------------------------------------------------- */

const NEXT: Record<ThemePref, ThemePref> = { light: 'dark', dark: 'system', system: 'light' };
const ICON: Record<ThemePref, string> = {
  light: 'bi-sun',
  dark: 'bi-moon',
  system: 'bi-circle-half',
};
const LABEL: Record<ThemePref, string> = {
  light: 'Theme: Light',
  dark: 'Theme: Dark',
  system: 'Theme: System',
};

function refreshControls(): void {
  // Guard: the first refresh() runs before the DOM exists.
  if (!document.body) return;
  const pref = getPref();
  for (const button of document.querySelectorAll<HTMLElement>('[data-theme-toggle]')) {
    button.title = `${LABEL[pref]} — click to change`;
    const icon = button.querySelector('i');
    if (icon) icon.className = `bi ${ICON[pref]}`;
  }
  for (const radio of document.querySelectorAll<HTMLInputElement>('input[name="theme-pref"]')) {
    radio.checked = radio.value === pref;
  }
  for (const surface of OVERRIDABLE) {
    const value = getOverride(surface);
    for (const radio of document.querySelectorAll<HTMLInputElement>(
      `input[name="theme-pref-${surface}"]`,
    )) {
      radio.checked = radio.value === value;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  refreshControls();
  document.addEventListener('click', (e) => {
    const target = e.target as Element;
    if (target.closest?.('[data-theme-toggle]')) {
      e.preventDefault();
      window.bhsTheme.set(NEXT[getPref()]);
    } else if (target.closest?.('[data-theme-match-site]')) {
      for (const surface of OVERRIDABLE) window.bhsTheme.setOverride(surface, 'site');
    }
  });
  document.addEventListener('change', (e) => {
    const radio = e.target as HTMLInputElement;
    if (!radio.checked || !radio.name) return;
    if (radio.name === 'theme-pref') {
      const v = radio.value;
      window.bhsTheme.set(v === 'light' || v === 'dark' ? v : 'system');
    } else if (radio.name.startsWith('theme-pref-')) {
      const surface = radio.name.slice('theme-pref-'.length);
      if (!isOverridable(surface)) return;
      const v = radio.value;
      window.bhsTheme.setOverride(surface, v === 'light' || v === 'dark' ? v : 'site');
    }
  });
});

export {};
